import type { Card, Chassis, Director, Driver, Rarity, RerollKeep, SeasonState } from "../types";
import { isChassis, isDriver } from "../types";
import { Rng } from "./rng";
import { DRIVERS } from "../data/drivers";
import { CHASSIS } from "../data/chassis";
import { DIRECTORS } from "../data/directors";
import { makeTeam } from "./team";
import { buildRivals } from "./rivals";
import {
  CALENDAR_SIZE,
  GRID_RIVALS,
  MYTHIC_STEPS,
  PREM_MAX,
  PREM_MIN,
  PREM_PITY,
  PREM_REROLL_PENALTY,
  PREM_START,
  TRACKS,
} from "../data/tracks";

// Rerolls are a single budget for the WHOLE draft (not per pack): the player
// spends them whenever they like — a full reroll, a per-card keep, or a director
// reroll — and a new pack never tops them back up. `rerollLeft` counts down from
// here to 0.
export const TOTAL_REROLLS = 4;

export interface DrawConstraint {
  country?: string;
  team?: string;
}

/**
 * Draw one card. The returned Card's `type` ALWAYS matches the pool it came
 * from — there is no path that wraps a driver row as a chassis. The old runtime
 * bug is now unrepresentable because Driver and Chassis are distinct types.
 */

/**
 * Identity of a card for within-pack de-duplication. A driver is keyed by
 * name+season, a chassis by its (unique) model. The type-tagged prefix keeps
 * the two namespaces from ever colliding.
 */
export const cardKey = (c: Card): string =>
  isChassis(c) ? `c|${c.model}` : `d|${c.name}|${c.team}`;

export function drawCard(
  rng: Rng,
  rarity: Rarity,
  constraint?: DrawConstraint,
  forceType?: "driver" | "chassis",
  excludeDrivers?: ReadonlySet<string>,
  excludeCards?: ReadonlySet<string>,
): Card {
  // Two independent exclusions:
  //  - excludeDrivers (HARD): driver NAMES the player already holds. Keyed by
  //    name (not name+season) so a lineup can never end up with two versions of
  //    the same driver — once you keep one Senna, every Senna leaves the pool.
  //    Chassis are unaffected (the single chassis slot blocks picks). Never
  //    relaxed.
  //  - excludeCards (SOFT): cards already drawn into the CURRENT pack — or, on a
  //    locked reroll, the locked card itself — so a reroll yields a DIFFERENT
  //    card when one exists. Relaxed only when nothing else qualifies.
  const notHeld = (c: Card): boolean => isChassis(c) || !excludeDrivers?.has(c.name);
  const notSeen = (c: Card): boolean => !excludeCards?.has(cardKey(c));
  const matches = (c: Card): boolean => {
    if (!constraint) return true;
    if (constraint.country != null) return isDriver(c) && c.country === constraint.country;
    if (constraint.team != null) return c.team === constraint.team;
    return true;
  };

  const wantChassis = forceType ? forceType === "chassis" : rng.next() < 0.34;

  // Candidate pools for one type, in descending priority. The forced type and
  // the held-driver rule are never violated; rarity, the lock constraint, and
  // the soft exclusion are progressively relaxed until something qualifies. The
  // lock constraint outranks rarity, which outranks repeating the same card —
  // so a locked reroll keeps the locked dimension and avoids repeats whenever
  // an alternative of the right type exists.
  const poolsFor = (chassis: boolean): readonly Card[][] => {
    const base = (chassis ? CHASSIS : DRIVERS).filter(notHeld);
    const atRarity = base.filter((c) => c.rarity === rarity);
    return [
      atRarity.filter(notSeen).filter(matches),
      base.filter(notSeen).filter(matches),
      atRarity.filter(matches),
      base.filter(matches),
      atRarity.filter(notSeen),
      base.filter(notSeen),
      atRarity,
      base,
    ].filter((p) => p.length > 0);
  };

  let pools = poolsFor(wantChassis);
  // Only switch type when the type was not forced and its pool is fully empty.
  if (pools.length === 0 && !forceType) pools = poolsFor(!wantChassis);
  if (pools.length === 0) {
    // Unreachable in practice (a forced type's full pool is never empty), but
    // keep the function total without ever violating the forced type.
    const all = (wantChassis ? CHASSIS : DRIVERS).filter(notHeld);
    return rng.pick(all.length ? all : wantChassis ? CHASSIS : DRIVERS);
  }
  return rng.pick(pools[0]!);
}

const PREMIUM_PLAN: readonly Rarity[] = ["mythic", "legend", "legend", "normal", "normal"];
const NORMAL_PLAN: readonly Rarity[] = ["legend", "legend", "normal", "normal", "normal"];

export function makePack(
  rng: Rng,
  premium: boolean,
  excludeDrivers?: ReadonlySet<string>,
): Card[] {
  const plan = premium ? PREMIUM_PLAN : NORMAL_PLAN;
  // Track cards already drawn this pack so no slot repeats a driver or chassis.
  const seen = new Set<string>();
  const pack: Card[] = [];
  for (const r of plan) {
    const card = drawCard(rng, r, undefined, undefined, excludeDrivers, seen);
    seen.add(cardKey(card));
    pack.push(card);
  }
  // Shuffle so rarity can't be read off card position — the plan always draws
  // the rarest card first, which would otherwise park Mythics at the top and
  // give away the best pick (especially in From Memory mode, where the rarity
  // badge is hidden).
  return rng.shuffle(pack);
}

/**
 * Names of drivers the player has already kept. Every version of a kept driver
 * (any season/team) is held out of future packs, so a lineup can't pair two
 * versions of the same driver.
 */
function heldDrivers(s: SeasonState): Set<string> {
  return new Set(s.picked.filter(isDriver).map((d) => d.name));
}

// ----------------------------------------------------------------------------
// Director pack — a separate, final draft step (not part of the Card draft).
// ----------------------------------------------------------------------------

export const DIRECTOR_PACK_SIZE = 4;

/**
 * Draw one director at the requested rarity, falling back to the full pool when
 * that tier is exhausted by `exclude` (so a pack is always fillable). Mirrors
 * drawCard's "relax rarity, never repeat within the pack" spirit, minus the
 * lock/forced-type machinery directors don't have.
 */
function drawDirector(rng: Rng, rarity: Rarity, exclude: ReadonlySet<string>): Director {
  const base = DIRECTORS.filter((d) => !exclude.has(d.name));
  const atRarity = base.filter((d) => d.rarity === rarity);
  return rng.pick(atRarity.length ? atRarity : base.length ? base : DIRECTORS);
}

// The director pack mirrors the elenco draft's PREMIUM_PLAN shape rather than
// rolling each slot independently: exactly ONE mythic slot, gated by the same
// ~PREM_START chance as a regular premium pack, so mythics appear about as often
// here as there — never two or three at once drowning out the normals (Steiner,
// the generics) that make a real difference now that independents carry a
// universal affinity. When the mythic slot misses, it falls back to a legend.
// A director mythic still does NOT count toward `mythicsPicked`.
const DIRECTOR_PLAN_BASE: readonly Rarity[] = ["legend", "normal", "normal"];

export function makeDirectorPack(rng: Rng): Director[] {
  const topSlot: Rarity = rng.next() < PREM_START ? "mythic" : "legend";
  const plan: readonly Rarity[] = [topSlot, ...DIRECTOR_PLAN_BASE];
  const seen = new Set<string>();
  const pack: Director[] = [];
  for (const rarity of plan) {
    const d = drawDirector(rng, rarity, seen);
    seen.add(d.name);
    pack.push(d);
  }
  // Shuffle for the same reason as makePack: rarity must not be readable off
  // card position (the mythic/legend slot is drawn first), especially in From Memory.
  return rng.shuffle(pack);
}

/** Roll the final director pack. Rerolls left carry over from the elenco draft. */
export function rollDirectorPack(rng: Rng, s: SeasonState): void {
  s.directorPack = makeDirectorPack(rng);
}

export interface Needs {
  drivers: number;
  chassis: number;
  needDriver: number;
  needChassis: number;
  // The director is needed ONLY once the 2 drivers + chassis are in — it's the
  // last pick. So early packs are unaffected and `needDirector` flips to 1 only
  // for the final director phase, then back to 0 once a director is chosen.
  needDirector: number;
}

export function needs(s: SeasonState): Needs {
  const drivers = s.picked.filter(isDriver).length;
  const chassis = s.picked.filter(isChassis).length;
  const needDriver = 2 - drivers;
  const needChassis = 1 - chassis;
  const coreComplete = needDriver < 1 && needChassis < 1;
  return {
    drivers,
    chassis,
    needDriver,
    needChassis,
    needDirector: coreComplete && !s.slots.director ? 1 : 0,
  };
}

/**
 * Guarantee the pack offers at least one card of every still-needed type, so a
 * pack can never lock the player out (e.g. both drivers kept, but the pack — or
 * a reroll of it — came back all-driver with no chassis). Applied after EVERY
 * pack regeneration, including rerolls. `protectedIndex` is never overwritten,
 * so a locked card survives the guarantee.
 */
function guaranteeNeeds(
  rng: Rng,
  s: SeasonState,
  held: ReadonlySet<string>,
  protectedIndex = -1,
): void {
  const n = needs(s);
  const ensure = (need: number, present: (c: Card) => boolean, force: "driver" | "chassis"): void => {
    if (need <= 0 || s.pack.some(present)) return;
    // Prefer overwriting a card that is NOT the needed type and not the lock.
    let i = s.pack.findIndex((c, idx) => idx !== protectedIndex && !present(c));
    if (i < 0) i = s.pack.findIndex((_, idx) => idx !== protectedIndex);
    if (i < 0) i = 0;
    const seen = new Set(s.pack.filter((_, idx) => idx !== i).map(cardKey));
    s.pack[i] = drawCard(rng, s.pack[i]!.rarity, undefined, force, held, seen);
  };
  ensure(n.needDriver, isDriver, "driver");
  ensure(n.needChassis, isChassis, "chassis");
}

/** Roll a fresh pack for the current slot, guaranteeing a needed-type card exists. */
export function rollPack(rng: Rng, s: SeasonState): void {
  const chance = Math.max(PREM_MIN, Math.min(PREM_MAX, s.premChance));
  const isPrem = rng.next() < chance;
  s.currentPremium = isPrem;
  s.currentChance = chance;

  const held = heldDrivers(s);
  s.pack = makePack(rng, isPrem, held);
  guaranteeNeeds(rng, s, held);
}

/** Update the running premium chance after a card is kept. */
export function updateChance(s: SeasonState, pickedMythic: boolean): void {
  if (pickedMythic) {
    s.mythicsPicked++;
    const ceil = MYTHIC_STEPS[Math.min(s.mythicsPicked, MYTHIC_STEPS.length - 1)]!;
    s.premChance = Math.min(s.premChance, ceil);
  } else if (!s.currentPremium) {
    s.premChance = Math.min(PREM_MAX, s.premChance + PREM_PITY);
  }
}

export function newSeasonState(teamName: string): SeasonState {
  return {
    teamName,
    chaos: false,
    fromMemory: false,
    seed: 0, // set at start time (random for Free Season, date-derived for Daily)
    daily: false,
    day: "",
    rngDraws: 0,
    picked: [],
    mythicsPicked: 0,
    rollNo: 0,
    currentPremium: false,
    currentChance: PREM_START,
    premChance: PREM_START,
    pack: [],
    directorPack: [],
    rerollLeft: TOTAL_REROLLS,
    slots: { d1: null, d2: null, chassis: null, director: null },
    calendar: [],
    weather: [],
    round: 0,
    results: [],
    opponents: [],
    standings: null,
  };
}

export function rerollPack(rng: Rng, s: SeasonState): void {
  if (s.rerollLeft < 1) return;
  s.rerollLeft -= 1;
  // A reroll re-flips the premium coin (it is NOT locked to the current pack's
  // tier). To stop players from burning rerolls fishing for a premium pack,
  // rerolling a NORMAL pack first drops the premium chance by PREM_REROLL_PENALTY
  // (floored at PREM_MIN); each further reroll of a normal pack costs another
  // step. Rerolling a premium pack carries no penalty — but re-flips at the
  // current chance, so it can itself come back normal.
  if (!s.currentPremium) {
    s.premChance = Math.max(PREM_MIN, s.premChance - PREM_REROLL_PENALTY);
  }
  const chance = Math.max(PREM_MIN, Math.min(PREM_MAX, s.premChance));
  const isPrem = rng.next() < chance;
  s.currentPremium = isPrem;
  s.currentChance = chance;
  const held = heldDrivers(s);
  s.pack = makePack(rng, isPrem, held);
  guaranteeNeeds(rng, s, held);
}

/**
 * Reroll the pack while keeping one trait of the card at `cardIndex`: its team
 * (drivers and chassis) or its country (drivers only). Every slot — including
 * the locked one — is redrawn at the rarity of a freshly re-flipped pack plan,
 * so a country/team lock lets the player FISH for a premium-rarity version of
 * that driver/team (drawCard keeps the constraint above rarity: it upgrades the
 * rarity only when a matching card of that nationality/team exists, otherwise it
 * relaxes the rarity, never the lock). The kept card is redrawn into a DIFFERENT
 * card sharing the trait when one exists, and every other slot is redrawn freely.
 * Consumes one of the draft's rerolls.
 */
export function rerollKeeping(
  rng: Rng,
  s: SeasonState,
  cardIndex: number,
  keep: RerollKeep,
): void {
  if (s.rerollLeft < 1) return;
  const locked = s.pack[cardIndex];
  if (!locked) return;
  // Country is a driver-only trait; ignore a stale country reroll on a chassis.
  if (keep === "country" && !isDriver(locked)) return;
  s.rerollLeft -= 1;

  // Re-flip the premium coin like rerollPack (with the same penalty when the
  // current pack is normal), so a kept-trait reroll can fish for a premium
  // version of the locked driver/team instead of being pinned to the current
  // card's rarity.
  if (!s.currentPremium) {
    s.premChance = Math.max(PREM_MIN, s.premChance - PREM_REROLL_PENALTY);
  }
  const chance = Math.max(PREM_MIN, Math.min(PREM_MAX, s.premChance));
  const isPrem = rng.next() < chance;
  s.currentPremium = isPrem;
  s.currentChance = chance;

  const held = heldDrivers(s);
  // One rarity per slot from the (re-flipped) pack plan, shuffled so rarity is
  // not readable off card position — matching makePack.
  const plan = rng.shuffle(isPrem ? PREMIUM_PLAN : NORMAL_PLAN);
  const constraint: DrawConstraint =
    keep === "team" ? { team: locked.team } : { country: (locked as Driver).country };
  // Preserve the kept card's TYPE: keeping a chassis must reroll into another
  // chassis, not a driver that happens to share the team (the old chassis-lock
  // bug). Exclude the current card so the reroll yields a DIFFERENT match when
  // the pool has one — drawCard relaxes that only if it's the sole candidate.
  const forceType = isChassis(locked) ? "chassis" : "driver";
  const lockedSeen = new Set<string>([cardKey(locked)]);
  const newLocked = drawCard(rng, plan[cardIndex]!, constraint, forceType, held, lockedSeen);

  const seen = new Set<string>([cardKey(newLocked)]);
  s.pack = s.pack.map((_c, i) => {
    if (i === cardIndex) return newLocked;
    const card = drawCard(rng, plan[i]!, undefined, undefined, held, seen);
    seen.add(cardKey(card));
    return card;
  });
  guaranteeNeeds(rng, s, held, cardIndex);
}

export type PickOutcome =
  | { ok: true; finalized: boolean }
  | { ok: false; reason: string };

/** Keep one card. Returns whether the draft is now complete (finalized). */
export function pickCard(rng: Rng, s: SeasonState, cardIndex: number): PickOutcome {
  const card = s.pack[cardIndex];
  if (!card) return { ok: false, reason: "No card at that index." };
  const n = needs(s);
  if (isDriver(card) && n.needDriver < 1) return { ok: false, reason: "You already have 2 drivers." };
  if (isChassis(card) && n.needChassis < 1) return { ok: false, reason: "You already have a chassis." };

  s.picked.push(card);
  updateChance(s, card.rarity === "mythic");

  if (isChassis(card)) s.slots.chassis = card;
  else if (!s.slots.d1) s.slots.d1 = card;
  else s.slots.d2 = card;

  const after = needs(s);
  if (after.needDirector > 0) {
    // Elenco complete — move to the final, mandatory director pick instead of
    // finalizing. The draft finalizes only once a director is chosen.
    rollDirectorPack(rng, s);
    s.rollNo++;
    return { ok: true, finalized: false };
  }
  rollPack(rng, s);
  s.rollNo++;
  return { ok: true, finalized: false };
}

/** Keep one director, completing the draft. */
export function pickDirector(rng: Rng, s: SeasonState, index: number): PickOutcome {
  const director = s.directorPack[index];
  if (!director) return { ok: false, reason: "No director at that index." };
  if (needs(s).needDirector < 1) return { ok: false, reason: "Pick your drivers and chassis first." };

  s.slots.director = director;
  finalizeDraft(rng, s);
  return { ok: true, finalized: true };
}

/** Reroll the whole director pack, consuming one of the draft's rerolls. */
export function rerollDirectorPack(rng: Rng, s: SeasonState): void {
  if (s.rerollLeft < 1) return;
  s.rerollLeft -= 1;
  s.directorPack = makeDirectorPack(rng);
}

export function finalizeDraft(rng: Rng, s: SeasonState): void {
  const d1 = s.slots.d1 as Driver;
  const d2 = s.slots.d2 as Driver;
  const chassis = s.slots.chassis as Chassis;
  const player = makeTeam({
    name: s.teamName || "My Team",
    d1,
    d2,
    chassis,
    director: s.slots.director,
    isPlayer: true,
  });
  // Rivals are normally drawn up front (drawRivals, alongside the calendar) so
  // the grid is fixed by the seed regardless of how much the player rerolled.
  // They sit in s.opponents as a rivals-only list until now; fall back to
  // drawing here for any path that didn't (e.g. an old save resumed mid-draft).
  const rivals = s.opponents.length ? s.opponents : buildRivals(rng, GRID_RIVALS);
  s.opponents = [player, ...rivals];
  // The calendar is normally drawn up front (drawCalendar, before the director
  // pick) so the player can weigh it. Fall back to drawing it here for any path
  // that didn't — e.g. an older save resumed mid-draft with an empty calendar.
  if (s.calendar.length === 0) drawCalendar(rng, s);
}

/**
 * Draw the season's calendar: CALENDAR_SIZE tracks from the full pool. Called at
 * the START of the draft (not at finalize) so the director phase can show the
 * track mix — the calendar is what makes the qualifying-specialist-vs-race-
 * strategist choice meaningful (street-heavy favours the qualifier).
 */
export function drawCalendar(rng: Rng, s: SeasonState): void {
  s.calendar = rng.shuffle(TRACKS).slice(0, CALENDAR_SIZE);
}

/**
 * Draw the rival grid up front (alongside the calendar) so it's fixed by the
 * seed and unaffected by how much the player rerolls. Stored in s.opponents as
 * a rivals-only list; finalizeDraft prepends the player team. Nothing reads
 * s.opponents before finalize, so the player-less interim is invisible.
 */
export function drawRivals(rng: Rng, s: SeasonState): void {
  s.opponents = buildRivals(rng, GRID_RIVALS);
}
